import { useEffect, useRef, useState } from "react";
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signOut,
} from "firebase/auth";
import { auth } from "firebase_/phone_auth_firebase";

/**
 * Firebase 전화번호 인증(SMS 인증번호) 모달.
 * 기존 Bootpay(다날 PASS) 본인인증을 대체한다.
 *
 * props
 *  - open        : 모달 표시 여부
 *  - onClose     : 닫기 콜백
 *  - onSuccess   : 인증 성공 시 호출. collectName 여부에 따라
 *                  { phone } 또는 { name, phone } 형태로 전달. (phone 예: "01012345678")
 *  - collectName : true 면 이름 입력란을 함께 노출하고 onSuccess 에 name 도 담아 반환
 *                  (회원가입처럼 이름이 필요한 화면용). PASS 인증과 달리 전화번호만
 *                  실제 인증되며 이름은 사용자 입력값이다.
 *  - initialName : collectName 일 때 이름 입력란 초기값(재인증 시 유지용)
 */

// 한국 휴대폰 번호(010xxxxyyyy) -> E.164(+8210xxxxyyyy)
const toE164 = (raw) => {
  const digits = (raw || "").replace(/\D/g, "");
  return "+82" + digits.replace(/^0/, "");
};

const isValidKoreanMobile = (raw) => {
  const digits = (raw || "").replace(/\D/g, "");
  return /^01[016789]\d{7,8}$/.test(digits);
};

const formatPhone = (raw) =>
  (raw || "").replace(/\D/g, "").replace(/^(\d{3})(\d{3,4})(\d{4})$/, "$1-$2-$3");

// 입력 중: 숫자만 남기고 자동으로 하이픈 삽입 (010-1234-5678, 최대 11자리)
const formatPhoneInput = (raw) => {
  const d = (raw || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
};

// Firebase 전화인증 에러코드 -> 사용자 메시지 (원인 파악 위해 코드도 노출)
const authErrorMessage = (err) => {
  const code = err?.code || "";
  switch (code) {
    case "auth/invalid-phone-number":
    case "auth/missing-phone-number":
      return "전화번호 형식이 올바르지 않습니다.";
    case "auth/quota-exceeded":
      return "SMS 발송 한도를 초과했습니다. 잠시 후 다시 시도해주세요.";
    case "auth/too-many-requests":
      return "요청이 너무 많습니다. 잠시 후 다시 시도해주세요.";
    case "auth/operation-not-allowed":
      return "전화 인증이 비활성화되어 있습니다. (Firebase 콘솔 > Authentication > Sign-in method > Phone 활성화 필요)";
    case "auth/billing-not-enabled":
      return "SMS 발송에 Firebase Blaze(종량제) 요금제가 필요합니다.";
    case "auth/unauthorized-domain":
    case "auth/captcha-check-failed":
    case "auth/invalid-app-credential":
    case "auth/internal-error":
      return "도메인 인증(reCAPTCHA)에 실패했습니다. (Firebase 콘솔 > Authentication > Settings > 승인된 도메인에 www.ddukddaki.com 추가 필요)";
    default:
      return `인증번호 전송에 실패했습니다. (${code || err?.message || "알 수 없는 오류"})`;
  }
};

// 인증번호 입력 제한시간(3분)
const AUTH_TIMEOUT_SEC = 180;
const mmss = (s) =>
  `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(
    2,
    "0"
  )}`;

export default function PhoneAuthDialog({
  open,
  onClose,
  onSuccess,
  collectName = false,
  initialName = "",
}) {
  const [step, setStep] = useState("input"); // "input" | "code"
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(0);

  const recaptchaRef = useRef(null);
  const verifierRef = useRef(null);
  const confirmationRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName(initialName || "");
    } else {
      resetAll();
    }
    return () => {
      clearVerifier();
      stopTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const clearVerifier = () => {
    if (verifierRef.current) {
      try {
        verifierRef.current.clear();
      } catch (e) {}
      verifierRef.current = null;
    }
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // 3분 카운트다운 시작 (전송/재전송 성공 시 호출)
  const startTimer = () => {
    stopTimer();
    setSecondsLeft(AUTH_TIMEOUT_SEC);
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          stopTimer();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const resetAll = () => {
    setStep("input");
    setName("");
    setPhone("");
    setCode("");
    setSending(false);
    setVerifying(false);
    setError("");
    confirmationRef.current = null;
    clearVerifier();
    stopTimer();
    setSecondsLeft(0);
  };

  const setupRecaptcha = () => {
    clearVerifier();
    // "reCAPTCHA has already been rendered in this element" 방지:
    // 이전 위젯이 남은 컨테이너를 재사용하면 에러가 나므로, 매번 비우고
    // grecaptcha 가 처음 보는 새 엘리먼트를 만들어 거기에 렌더링한다.
    const wrapper = recaptchaRef.current;
    let host = wrapper;
    if (wrapper) {
      wrapper.innerHTML = "";
      host = document.createElement("div");
      wrapper.appendChild(host);
    }
    verifierRef.current = new RecaptchaVerifier(auth, host, {
      size: "invisible",
    });
    return verifierRef.current;
  };

  // 인증번호 전송
  const handleSendCode = async () => {
    setError("");
    if (collectName && !name.trim()) {
      setError("이름을 입력해주세요.");
      return;
    }
    if (!isValidKoreanMobile(phone)) {
      setError("올바른 휴대폰 번호를 입력해주세요. (예: 010-1234-5678)");
      return;
    }
    setSending(true);
    try {
      const verifier = setupRecaptcha();
      const confirmation = await signInWithPhoneNumber(
        auth,
        toE164(phone),
        verifier
      );
      confirmationRef.current = confirmation;
      setCode("");
      setStep("code");
      startTimer();
    } catch (err) {
      console.error("인증번호 전송 실패:", err);
      setError(authErrorMessage(err));
      clearVerifier();
    } finally {
      setSending(false);
    }
  };

  // 인증번호 확인
  const handleVerifyCode = async () => {
    setError("");
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setError("인증번호 6자리를 정확히 입력해주세요.");
      return;
    }
    if (secondsLeft === 0) {
      setError("인증 시간이 만료되었습니다. 인증번호를 다시 받아주세요.");
      return;
    }
    if (!confirmationRef.current) {
      setError("인증 세션이 만료되었습니다. 다시 시도해주세요.");
      setStep("input");
      return;
    }
    setVerifying(true);
    try {
      await confirmationRef.current.confirm(trimmed);
      stopTimer();
      const digits = phone.replace(/\D/g, "");
      const payload = collectName
        ? { name: name.trim(), phone: digits }
        : { phone: digits };
      onSuccess && onSuccess(payload);
      // 인증 용도로만 로그인했으므로 파이어베이스 세션 정리 (앱 자체 로그인과 무관)
      signOut(auth).catch(() => {});
      onClose && onClose();
    } catch (err) {
      console.error("인증번호 확인 실패:", err);
      setError("인증번호가 올바르지 않습니다. 다시 확인해주세요.");
    } finally {
      setVerifying(false);
    }
  };

  if (!open) return null;

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={headerRow}>
          <div style={title}>{collectName ? "본인인증" : "휴대폰 인증"}</div>
          <div style={closeBtn} onClick={onClose}>
            ✕
          </div>
        </div>

        {step === "input" ? (
          <>
            {collectName && (
              <>
                <div style={label}>이름</div>
                <input
                  type="text"
                  placeholder="이름을 입력해주세요"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  style={{ ...input, marginBottom: 14 }}
                  autoFocus
                />
              </>
            )}
            <div style={label}>휴대폰 번호</div>
            <input
              type="tel"
              inputMode="numeric"
              placeholder="010-1234-5678"
              value={phone}
              onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !sending) handleSendCode();
              }}
              style={input}
              autoFocus={!collectName}
            />
            {error && <div style={errorText}>{error}</div>}
            <button
              type="button"
              onClick={handleSendCode}
              disabled={sending}
              style={{ ...primaryBtn, opacity: sending ? 0.6 : 1 }}
            >
              {sending ? "전송 중..." : "인증번호 전송"}
            </button>
          </>
        ) : (
          <>
            <div style={infoText}>
              <b>{formatPhone(phone)}</b> 번호로 전송된
              <br />
              인증번호 6자리를 입력해주세요.
            </div>
            <input
              type="tel"
              inputMode="numeric"
              maxLength={6}
              placeholder="인증번호 6자리"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !verifying && secondsLeft > 0)
                  handleVerifyCode();
              }}
              style={{ ...input, letterSpacing: "4px", textAlign: "center" }}
              autoFocus
            />
            <div style={timerRow}>
              {secondsLeft > 0 ? (
                <span style={timerText}>입력 제한시간 {mmss(secondsLeft)}</span>
              ) : (
                <span style={expiredText}>
                  인증 시간이 만료되었습니다. 인증번호를 다시 받아주세요.
                </span>
              )}
            </div>
            {error && <div style={errorText}>{error}</div>}
            <button
              type="button"
              onClick={handleVerifyCode}
              disabled={verifying || secondsLeft === 0}
              style={{
                ...primaryBtn,
                opacity: verifying || secondsLeft === 0 ? 0.6 : 1,
                cursor: secondsLeft === 0 ? "not-allowed" : "pointer",
              }}
            >
              {verifying ? "확인 중..." : "인증 확인"}
            </button>
            <button
              type="button"
              onClick={handleSendCode}
              disabled={sending}
              style={resendBtn}
            >
              {sending ? "재전송 중..." : "인증번호 다시 받기"}
            </button>
          </>
        )}

        {/* invisible reCAPTCHA 컨테이너 */}
        <div ref={recaptchaRef} />
      </div>
    </div>
  );
}

const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 2147483000,
  padding: "16px",
};

const card = {
  background: "#FFFFFF",
  borderRadius: "16px",
  padding: "24px",
  width: "360px",
  maxWidth: "90vw",
  boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
  fontFamily:
    'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  boxSizing: "border-box",
};

const headerRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "20px",
};

const title = { fontSize: "18px", fontWeight: 700, color: "#1F2937" };

const closeBtn = {
  cursor: "pointer",
  fontSize: "18px",
  color: "#6B7280",
  lineHeight: 1,
  padding: "4px",
};

const label = {
  fontSize: "14px",
  fontWeight: 600,
  color: "#374151",
  marginBottom: "8px",
};

const infoText = {
  fontSize: "14px",
  color: "#4B5563",
  marginBottom: "16px",
  lineHeight: 1.5,
};

const input = {
  width: "100%",
  height: "48px",
  padding: "0 14px",
  fontSize: "15px",
  border: "1px solid #D1D5DB",
  borderRadius: "8px",
  boxSizing: "border-box",
  outline: "none",
  fontFamily: "inherit",
};

const primaryBtn = {
  width: "100%",
  height: "48px",
  marginTop: "16px",
  background: "#047857",
  color: "#FFFFFF",
  fontSize: "15px",
  fontWeight: 600,
  border: "none",
  borderRadius: "8px",
  cursor: "pointer",
};

const resendBtn = {
  width: "100%",
  marginTop: "10px",
  background: "transparent",
  color: "#047857",
  fontSize: "13px",
  fontWeight: 500,
  border: "none",
  cursor: "pointer",
  textDecoration: "underline",
};

const errorText = {
  color: "#DC2626",
  fontSize: "13px",
  marginTop: "8px",
};

const timerRow = {
  marginTop: "10px",
  textAlign: "center",
};

const timerText = {
  fontSize: "13px",
  color: "#047857",
  fontWeight: 600,
};

const expiredText = {
  fontSize: "13px",
  color: "#DC2626",
  fontWeight: 600,
};
